"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextBaseField = void 0;
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const utils_1 = require("../../model/utils");
const types_1 = require("../../types");
const field_types_1 = require("../../types/field_types");
const utils_2 = require("../../utils");
const field_1 = require("./field");
const stat_1 = require("./stat");
class TextBaseField extends field_1.Field {
    validateCellValue(cv) {
        return TextBaseField.cellValueSchema.validate(cv);
    }
    validateOpenWriteValue(owv) {
        return TextBaseField.openWriteValueSchema.validate(owv);
    }
    validateProperty() {
        return TextBaseField.propertySchema.validate(this.field.property);
    }
    get apiMetaProperty() {
        return null;
    }
    get openValueJsonSchema() {
        return {
            type: 'string',
            title: this.field.name,
        };
    }
    showFOperatorDesc(type) {
        return types_1.FOperatorDescMap[type];
    }
    get acceptFilterOperators() {
        return TextBaseField._acceptFilterOperators;
    }
    get basicValueType() {
        return field_types_1.BasicValueType.String;
    }
    cellValueToStdValue(cellValue) {
        const stdValue = {
            sourceType: field_types_1.FieldType.Text,
            data: [],
        };
        if (cellValue) {
            stdValue.data = cellValue.map(seg => (0, utils_2.fastCloneDeep)(seg));
        }
        return stdValue;
    }
    stdValueToCellValue(stdField) {
        const { data, sourceType } = stdField;
        if (data.length === 0) {
            return null;
        }
        let segments;
        if ([field_types_1.FieldType.MultiSelect, field_types_1.FieldType.Link, field_types_1.FieldType.Attachment, field_types_1.FieldType.Member].includes(sourceType)) {
            segments = [{ type: field_types_1.SegmentType.Text, text: data.map(d => d.text).join(', ') }];
        }
        else if (sourceType === field_types_1.FieldType.Text) {
            segments = data.map(d => (Object.assign({ type: d.type || field_types_1.SegmentType.Text }, d)));
        }
        else {
            segments = [{ type: field_types_1.SegmentType.Text, text: data.map(d => d.text).join('') }];
        }
        if (segments.length === 0 || segments.every(segment => segment.text === '')) {
            return null;
        }
        return segments;
    }
    validate(value) {
        if (Array.isArray(value)) {
            return value.every(segment => {
                return segment != null && (0, lodash_1.isNumber)(segment.type) && (0, lodash_1.isString)(segment.text);
            });
        }
        return false;
    }
    defaultValueForCondition(condition) {
        // There is no default padding logic for text type filtering\
        const { value } = condition;
        return value ? value.map(item => ({ text: item, type: field_types_1.SegmentType.Text })) : null;
    }
    cellValueToString(cellValue) {
        if (cellValue == null) {
            return null;
        }
        const cv = [cellValue].flat();
        return cv.map(seg => seg.text).join('') || null;
    }
    static stringInclude(str, searchStr) {
        return str.toLowerCase().includes(searchStr.trim().toLowerCase());
    }
    // The formatted string is directly passed in here
    static _isMeetFilter(operator, cellText, conditionValue, optFn) {
        if (operator === types_1.FOperator.IsEmpty) {
            return cellText == null;
        }
        if (operator === types_1.FOperator.IsNotEmpty) {
            return cellText != null;
        }
        if (conditionValue === null) {
            return true;
        }
        const [filterValue] = conditionValue;
        const { containsFn, defaultFn, doesNotContainFn } = optFn || {};
        switch (operator) {
            case types_1.FOperator.Is: {
                return cellText != null && cellText.trim().toLowerCase() === filterValue.trim().toLowerCase();
            }
            case types_1.FOperator.IsNot: {
                return cellText == null || cellText.trim().toLowerCase() !== filterValue.trim().toLowerCase();
            }
            case types_1.FOperator.Contains: {
                if (containsFn) {
                    return containsFn(filterValue);
                }
                return cellText != null && TextBaseField.stringInclude(cellText, filterValue);
            }
            case types_1.FOperator.In: {
                return cellText != null && TextBaseField.stringInclude(filterValue, cellText);
            }
            case types_1.FOperator.DoesNotContain: {
                if (doesNotContainFn) {
                    return doesNotContainFn(filterValue);
                }
                return cellText == null || !TextBaseField.stringInclude(cellText, filterValue);
            }
            default: {
                if (defaultFn) {
                    return defaultFn();
                }
                return false;
            }
        }
    }
    eq(cv1, cv2) {
        return this.cellValueToString(cv1) === this.cellValueToString(cv2);
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        const cellText = this.cellValueToString(cellValue);
        return TextBaseField._isMeetFilter(operator, cellText, conditionValue, {
            containsFn: (filterValue) => cellText != null && this.stringInclude(cellText, filterValue),
            doesNotContainFn: (filterValue) => cellText == null || !this.stringInclude(cellText, filterValue),
            defaultFn: () => super.isMeetFilter(operator, cellValue, conditionValue)
        });
    }
    cellValueToApiStandardValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToApiStringValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    openWriteValueToCellValue(openWriteValue) {
        if ((0, utils_1.isNullValue)(openWriteValue)) {
            return null;
        }
        return (0, utils_2.string2Segment)(openWriteValue);
    }
    validateAddOpenFieldProperty(updateProperty) {
        if (this.field.type !== field_types_1.FieldType.SingleText && updateProperty === null) {
            return { error: undefined, value: null };
        }
        return this.validateUpdateOpenProperty(updateProperty);
    }
    static _filterValueToOpenFilterValue(value) {
        var _a;
        return (_a = value === null || value === void 0 ? void 0 : value[0]) !== null && _a !== void 0 ? _a : null;
    }
    filterValueToOpenFilterValue(value) {
        return TextBaseField._filterValueToOpenFilterValue(value);
    }
    static _openFilterValueToFilterValue(value) {
        return value === null ? null : [value];
    }
    openFilterValueToFilterValue(value) {
        return TextBaseField._openFilterValueToFilterValue(value);
    }
    static _validateOpenFilterValue(value) {
        return TextBaseField.validateOpenFilterSchema.validate(value);
    }
    validateOpenFilterValue(value) {
        return TextBaseField._validateOpenFilterValue(value);
    }
}
exports.TextBaseField = TextBaseField;
TextBaseField._statTypeList = [
    stat_1.StatType.None,
    stat_1.StatType.CountAll,
    stat_1.StatType.Empty,
    stat_1.StatType.Filled,
    stat_1.StatType.Unique,
    stat_1.StatType.PercentEmpty,
    stat_1.StatType.PercentFilled,
    stat_1.StatType.PercentUnique,
];
TextBaseField._acceptFilterOperators = [
    types_1.FOperator.Is,
    types_1.FOperator.IsNot,
    types_1.FOperator.Contains,
    types_1.FOperator.DoesNotContain,
    types_1.FOperator.IsEmpty,
    types_1.FOperator.IsNotEmpty,
    types_1.FOperator.IsRepeat,
    types_1.FOperator.In,
];
TextBaseField.cellValueSchema = joi_1.default.array().items(joi_1.default.object({
    text: joi_1.default.string().allow('').required(),
    type: joi_1.default.number().required(),
    link: joi_1.default.string(),
}).required()).allow(null).required();
TextBaseField.openWriteValueSchema = joi_1.default.string().allow(null).required();
TextBaseField.propertySchema = joi_1.default.equal(null);
TextBaseField.validateOpenFilterSchema = joi_1.default.string().allow(null);
//# sourceMappingURL=text_base_field.js.map